'use client';

import { motion, useAnimationControls } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useEffect } from 'react';


const MarqueeFrame = ({
  items,  
  reverse,
  duration,
  gap,
  controls,
}) => (
  <motion.div
    className="flex shrink-0"
    style={{ gap: `${gap}px` }}
    animate={controls}
    initial={{ x: `${!reverse ? 0 : '-100%'}` }}
    transition={{ 
      duration, 
      repeat: Infinity, 
      ease: 'linear'
    }}
  >
    {items.map((item, index) => (
      <div 
        key={index} 
        className="flex shrink-0 whitespace-nowrap py-0"
        style={{ 
          marginRight: index === items.length - 1 ? `${gap}px` : 0,
          marginLeft: index === 0 ? `${gap}px` : 0 
        }}
      >
        {item}
      </div>
    ))}
  </motion.div>
);

export const Marquee = ({
  items,
  reverse = false,
  duration = 20,
  gap = 16,
  showGradient = true,
  pauseOnHover = false,
  title,
  titleHref,
  className,
  contentClassName,
  titleClassName,
}) => {
  const controls = useAnimationControls();

  useEffect(() => {
    controls.start({
      x: !reverse ? '-100%' : 0,
      transition: {
        duration,
        repeat: Infinity,
        ease: 'linear'
      }
    });
  }, [controls, reverse, duration]);

  const handleMouseEnter = () => {
    if (pauseOnHover) {
      controls.stop();
    }
  };

  const handleMouseLeave = () => {
    if (pauseOnHover) {
      controls.start({
        x: !reverse ? '-100%' : 0,
        transition: {
          duration,
          repeat: Infinity,
          ease: 'linear'
        }
      });
    }
  };

  return (
    <div 
      className={cn("w-full", className)}
    >
      <div className={cn("flex items-center", contentClassName)}>
        {title && (
          <motion.h3 
            className="text-sm font-medium text-violet-500 mr-4 sm:mr-8 md:mr-12 lg:mr-16 shrink-0 z-10 bg-transparent px-0 max-w-[100px] sm:max-w-none text-wrap"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {titleHref ? (
              <a href={titleHref} className={cn("hover:text-violet-600 transition-colors", titleClassName)}>
                {title}
              </a>
            ) : (
              title
            )}
          </motion.h3>
        )}
        <div 
          className={cn("overflow-x-hidden flex-1" )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div
            className="flex"
            style={{
              maskImage: showGradient
                ? `linear-gradient(
            to right,
            rgba(0, 0, 0, 0),
            rgba(0, 0, 0, 1) 20%,
            rgba(0, 0, 0, 1) 80%,
            rgba(0, 0, 0, 0)
          )`
                : undefined,
            }}
          >
            {/* Two MarqueeFrame components create seamless infinite loop effect. */}
            <MarqueeFrame
              items={items}
              reverse={reverse}
              duration={duration}
              gap={gap}
              controls={controls}
            />
            <MarqueeFrame
              items={items}
              reverse={reverse}
              duration={duration}
              gap={gap}
              controls={controls}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Marquee;